<template>
  <div>
    <top-bar title="Status do rastreamento"/>

    <div class="container page-content">
      <div class="row mb-3">
        <div class="col-6 pr-1">
          <div class="card status-card h-100">
            <div class="card-body p-3">
              <div class="small text-muted">GPS</div>
              <div class="font-weight-bold">
                <span class="status-dot" :class="gpsStatusClass"></span>
                {{ gpsStatusLabel }}
              </div>
            </div>
          </div>
        </div>

        <div class="col-6 pl-1">
          <div class="card status-card h-100">
            <div class="card-body p-3">
              <div class="small text-muted">Servidor</div>
              <div class="font-weight-bold">
                <span class="status-dot" :class="serverStatusClass"></span>
                {{ serverStatusLabel }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="small text-muted mb-3">
        Modo: <b>{{ tracking.mode === 'native' ? 'Android nativo' : 'Web' }}</b>
        · Rastreamento: <b>{{ tracking.active ? 'ativo' : 'pausado' }}</b>
      </div>

      <div id="mapContainer" v-if="mapCenter">
        <l-map :zoom="zoom" :center="mapCenter" :options="mapOptions" @ready="mapReady">
          <l-tile-layer :url="url" :attribution="attribution"/>
          <l-marker :lat-lng="mapPosition" :icon="driverMarkerIcon"/>
        </l-map>
      </div>

      <div class="alert alert-warning mt-3 mb-2" v-if="pendingPosition && tracking.retryAt">
        <div class="font-weight-bold">Posição aguardando envio</div>
        <div class="small">Capturada em {{ formatTimestamp(pendingPosition.timestamp) }}</div>
        <div class="small">Nova tentativa prevista para {{ formatTimestamp(tracking.retryAt) }}</div>
      </div>

      <div class="card mt-3" v-if="currentPosition">
        <div class="card-body">
          <h6 class="font-weight-bold">Última posição capturada</h6>
          <div>{{ currentPosition.coords.latitude }}, {{ currentPosition.coords.longitude }}</div>
          <div class="small text-muted">Precisão: {{ positionAccuracy ? positionAccuracy + ' m' : 'não informada' }}</div>
          <div class="small text-muted">Capturada em {{ formatTimestamp(currentPosition.timestamp) }}</div>
          <div class="small text-muted" v-if="tracking.lastSentAt">Último envio em {{ formatTimestamp(tracking.lastSentAt) }}</div>
        </div>
      </div>

      <div class="alert alert-danger mt-3" v-if="tracking.lastError">
        <div class="font-weight-bold">Último erro</div>
        <div>{{ tracking.lastError.message }}</div>
        <div class="small">{{ formatTimestamp(tracking.lastError.timestamp) }}</div>
      </div>

      <button class="btn btn-info btn-block mt-3" @click="openErrors()">
        Ver últimos erros
      </button>
    </div>

    <modal :opened="modalErrors" @close="modalErrors = false">
      <div>
          <h6 class="font-weight-bold mb-3">Últimos erros</h6>
        <div class="table-responsive" v-if="errors.length">
          <table class="table table-sm table-bordered text-theme">
            <thead>
              <tr class="small">
                <th style="width: 140px">Data/Hora</th>
                <th>Erro</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(error, index) in errors" :key="index">
                <td>{{ formatTimestamp(error.timestamp) }}</td>
                <td>
                  <span class="badge badge-secondary mr-1">{{ error.source || 'gps' }}</span>
                  {{ formatError(error.msg) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="text-muted" v-else>Nenhum erro registrado.</p>
        <button class="btn btn-primary btn-block" v-if="errors.length" @click="clearErrors()">Limpar erros</button>
      </div>
    </modal>
  </div>
</template>

<script>
import TopBar from '@/components/TopBar';
import Modal from '@/components/Modal';
import { icon, latLng } from 'leaflet';
import { LMap, LMarker, LTileLayer } from 'vue2-leaflet';

export default {
  name: 'LogRastreamento',
  components: { Modal, TopBar, LMap, LMarker, LTileLayer },

  data() {
    return {
      errors: [],
      modalErrors: false,
      map: null,
      mapCenter: null,
      driverMarkerIcon: icon({
        iconUrl: require('../assets/icones/moto.png'),
        iconSize: [50, 50],
        iconAnchor: [25, 25]
      }),
      zoom: 16,
      url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
      mapOptions: {
        zoomSnap: 0.5,
        minZoom: 11,
        maxZoom: 18,
        scrollWheelZoom: 'center',
        touchZoom: 'center',
        doubleClickZoom: 'center'
      }
    };
  },

  methods: {
    mapReady(map) {
      this.map = map;

      if (this.mapPosition) {
        this.map.setView(this.mapPosition, this.zoom);
      }
    },

    followPosition(targetPosition) {
      if (this.map) {
        this.map.panTo(targetPosition, {
          animate: true,
          duration: 0.8,
          easeLinearity: 0.35
        });
      }
    },

    openErrors() {
      this.errors = this.readJson('errorLog', []).reverse();
      this.modalErrors = true;
    },

    clearErrors() {
      localStorage.removeItem('errorLog');
      this.errors = [];
    },

    readJson(key, fallback) {
      try {
        return JSON.parse(localStorage.getItem(key)) || fallback;
      } catch (error) {
        localStorage.removeItem(key);
        return fallback;
      }
    },

    formatTimestamp(timestamp) {
      return timestamp ? new Date(timestamp).toLocaleString() : 'não informado';
    },

    formatError(error) {
      if (!error) return 'Erro não informado.';
      if (typeof error === 'string') return error;
      return error.message || error.code || JSON.stringify(error);
    }
  },

  computed: {
    tracking() {
      return this.$store.state.tracking;
    },

    empresa() {
      return this.$store.state.user;
    },

    currentPosition() {
      return this.tracking.lastCapturedPosition || this.pendingPosition;
    },

    pendingPosition() {
      return this.tracking.pendingPosition;
    },

    mapPosition() {
      if (!this.currentPosition) return null;
      return latLng(this.currentPosition.coords.latitude, this.currentPosition.coords.longitude);
    },

    positionAccuracy() {
      return this.currentPosition?.coords?.accuracy || null;
    },

    gpsStatusLabel() {
      return {
        available: 'Disponível',
        unavailable: 'Indisponível',
        denied: 'Permissão negada'
      }[this.tracking.gpsStatus] || 'Aguardando';
    },

    gpsStatusClass() {
      return this.tracking.gpsStatus === 'available' ? 'status-ok' :
        this.tracking.gpsStatus ? 'status-error' : 'status-waiting';
    },

    serverStatusLabel() {
      if (this.tracking.mode === 'native') return 'Android';
      return {
        online: 'Conectado',
        offline: 'Falha no envio'
      }[this.tracking.serverStatus] || 'Aguardando';
    },

    serverStatusClass() {
      if (this.tracking.mode === 'native' || !this.tracking.serverStatus) return 'status-waiting';
      return this.tracking.serverStatus === 'online' ? 'status-ok' : 'status-error';
    }
  },

  watch: {
    mapPosition: {
      immediate: true,
      handler(position) {
        if (!position) return;

        if (!this.mapCenter) {
          this.mapCenter = latLng(position.lat, position.lng);
          return;
        }

        this.followPosition(position);
      }
    }
  }
};
</script>

<style scoped>
.status-card {
  border-radius: 12px;
}

.status-dot {
  display: inline-block;
  width: 9px;
  height: 9px;
  margin-right: 4px;
  border-radius: 50%;
}

.status-ok { background: #28a745; }
.status-error { background: #dc3545; }
.status-waiting { background: #ffc107; }

#mapContainer {
  position: relative;
  height: 42vh;
  min-height: 280px;
  overflow: hidden;
  border-radius: 12px;
}

</style>
